<script setup>
import {computed, onMounted, onUnmounted, ref, watch} from 'vue'
import {useStore} from 'vuex'
import {useRoute} from 'vue-router'
import { MapSource, MapLayer } from '@/components/map'
import { Spinner } from '@/components/ui/spinner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {CheckCircle2, XCircle} from 'lucide-vue-next'
import { API, HOST } from '@/utils/http'
import { getBaseUrl } from '@/utils/url'

const host = getBaseUrl()

const store = useStore()
const route = useRoute()
const map = computed(() => store.getters['geo/getMap'])

const emit = defineEmits(['isLoaded', 'statusUpdated'])
const props = defineProps({
  map: {
    type: Object,
    required: true
  },
  layerId: {
    type: String,
    required: true
  },
  beforeId: {
    type: String,
    required: true
  },
  groupId: {
    type: String,
    required: true
  },
  options: {
    type: Object,
    required: false,
    default: () => ({})
  },
  layer: {
    type: Object,
    required: false,
    default: () => ({})
  }
})

const statusToLabel = {
  '4': 'Creating tiles',
  '3': 'Waiting for result',
  '2': 'Activating',
  '1': 'Initializing',
  '0': 'Done',
  '-1': 'Failed: Initialization',
  '-2': 'Failed: Activation',
  '-3': 'Failed: Waiting for result',
  '-4': 'Failed: Creating tiles',
}

const currentStatus = ref(props.layer?.layer?.status ?? props.layer?.status ?? 0)
const pollingInterval = ref(null)
const orgId = computed(() => route.params.org_id)
const selectedProductBundle = ref('visual')

const planetOrderId = computed(() => {
  return props.layer?.layer?.planet_order_id
})

// Determine available product bundles
// Note: Geometry rendering is now handled in the parent LayerItemMapLayer component

const availableProductBundles = computed(() => {
  const bundles = new Set()

  // Check for new multi-product format
  if (props.layer?.layer?.data?.products) {
    props.layer.layer.data.products.forEach(product => {
      if (product.product_bundle) {
        bundles.add(product.product_bundle)
      }
    })
  }
  // Fall back to legacy single product format
  else if (props.layer?.layer?.data?.product_bundle) {
    bundles.add(props.layer.layer.data.product_bundle)
  }

  // Default to visual if nothing found
  if (bundles.size === 0) {
    bundles.add('visual')
  }

  return Array.from(bundles)
})

const hasMultipleProducts = computed(() => {
  return availableProductBundles.value.length > 1
})

// Track tile errors to reduce console spam
const tileErrorCount = ref(0)
// Limit tile error logging to 5 to avoid excessive console spam during tile loading failures
const MAX_CONSECUTIVE_TILE_ERRORS_TO_AVOID_SPAM = 5

const fetchLayerStatus = async () => {
  if (!props.layer.code || !orgId.value) return

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId.value}/layers/${props.layerId.replace(/^layer-/, '')}`
    })

    if (response.data && response.data.layer) {
      const newStatus = response.data.layer.status
      if (newStatus !== currentStatus.value) {
        currentStatus.value = newStatus
        emit('statusUpdated', newStatus)

        // Update the layer in the store
        store.dispatch('layers/updateOrgLayer', {
          orgId: orgId.value,
          layer: response.data
        })

        // Stop polling if status is complete (0) or failed (negative)
        if (newStatus === 0 || newStatus < 0) {
          stopPolling()
        }
      }
    }
  } catch (error) {
    console.error('Error fetching layer status:', error)
  }
}

const startPolling = () => {
  // Only poll if status is in progress (positive and not 0)
  if (currentStatus.value > 0) {
    // Poll every 30 seconds
    pollingInterval.value = setInterval(fetchLayerStatus, 30000)
    // Also fetch immediately
    fetchLayerStatus()
  }
}

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value)
    pollingInterval.value = null
  }
}

// Watch for product bundle changes to refresh the map source
watch(selectedProductBundle, (newBundle) => {
  if (map.value && parseInt(currentStatus.value) === 0) {
    // Reset error count when switching bundles
    tileErrorCount.value = 0

    // Force map to reload tiles with new product bundle
    const sourceId = `${props.layerId}-test-tiff-tiles-source`
    if (map.value.getSource(sourceId)) {
      // Update tiles URL
      const newTiles = [`${host}/tulum/tiles/orders/${planetOrderId.value}/{z}/{x}/{y}.png?product_bundle=${newBundle}`]
      map.value.getSource(sourceId).setTiles(newTiles)
    }
  }
})

// Setup tile error handling
const setupTileErrorHandling = () => {
  if (!map.value) return

  // Listen for tile errors and suppress console spam
  const sourceId = `${props.layerId}-test-tiff-tiles-source`

  const handleSourceError = (e) => {
    if (e.source?.id === sourceId && e.error?.status === 404) {
      // Only log first few errors to avoid spam
      if (tileErrorCount.value < MAX_CONSECUTIVE_TILE_ERRORS_TO_AVOID_SPAM) {
        tileErrorCount.value++
        if (tileErrorCount.value === MAX_CONSECUTIVE_TILE_ERRORS_TO_AVOID_SPAM) {
          console.warn(`Suppressing further 404 tile errors for layer ${props.layerId}`)
        }
      }
      // Prevent default error handling to suppress console error
      e.preventDefault?.()
    }
  }

  map.value.on('error', handleSourceError)

  // Store handler for cleanup
  return () => {
    map.value?.off('error', handleSourceError)
  }
}

let cleanupErrorHandler = null

onMounted(async () => {
  emit('isLoaded', true)
  // Set initial product bundle
  if (availableProductBundles.value.length > 0) {
    selectedProductBundle.value = availableProductBundles.value[0]
  }

  // Setup error handling when map is ready
  if (map.value && parseInt(currentStatus.value) === 0) {
    cleanupErrorHandler = setupTileErrorHandling()
  }

  startPolling()
})

onUnmounted(() => {
  stopPolling()
  // Clean up error handler
  cleanupErrorHandler?.()
})
</script>

<template>
  <div>
    <!-- Status indicator -->
    <div class="flex items-center my-1" v-if="parseInt(currentStatus) !== 0">
      <div class="mr-2">
        <XCircle v-if="parseInt(currentStatus) < 0" class="text-red-400 w-5 h-5" />
        <Spinner v-else class="w-5 h-5" />
      </div>
      <h4>{{ statusToLabel[currentStatus] }}</h4>
    </div>

    <!-- Product bundle selector for completed orders -->
    <div v-if="parseInt(currentStatus) === 0 && hasMultipleProducts" class="my-2">
      <div class="flex items-center space-x-2">
        <span class="text-sm font-medium">View:</span>
        <div class="flex space-x-1">
          <Button
            v-for="bundle in availableProductBundles"
            :key="bundle"
            size="sm"
            :variant="selectedProductBundle === bundle ? 'default' : 'outline'"
            @click="selectedProductBundle = bundle"
            class="text-xs"
          >
            {{ bundle === 'analytic_sr_udm2' ? 'Analytic (NDVI)' : 'Visual' }}
          </Button>
        </div>
      </div>
    </div>
  <div v-if="map">
    <!-- Raster tiles -->
    <MapSource
      v-if="parseInt(currentStatus) === 0 && planetOrderId"
      :map="map"
      type="raster"
      :id="`${layerId}-test-tiff-tiles-source`"
      :options="{
        tiles: [`${host}/tulum/tiles/orders/${planetOrderId}/{z}/{x}/{y}.png?product_bundle=${selectedProductBundle}`],
        tileSize: 256,
      }"
    >
      <MapLayer
        :map="map"
        :id="`${layerId}-test-tiff-tiles-layer`"
        type="raster"
        :source="`${layerId}-test-tiff-tiles-source`"
        :before-id="beforeId"
        :group-id="groupId"
        :options="options"
      ></MapLayer>
    </MapSource>
  </div>
  </div>
</template>
